// Diagnose Mode: Standalone (Mode 1) and VDR-invoked (Mode 2). Legacy layout for /user routes.
// Mode 3 uses WorkflowLayoutComponent + a new project-sessions container, not this layout.
import { AsyncPipe, NgClass } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, fromEvent, map, Observable, Subscription } from 'rxjs';
import { selectScreenState } from 'src/app/core/store/selectors/select-challenge.selectors';
import { ManageSessionService } from '../../../core/admin-features/services/diagnosis-session/manage-session.service';
import { HeaderComponent } from '../../../core/common/components/header/header.component';
import { StepMasterComponent } from '../../../core/common/components/step-master/step-master.component';
import { SOURCE } from '../../../core/common/constants/routes.constant';
import {
  CanComponentDeactivate,
  CanDeactivateType,
} from '../../../core/common/models/general.model';

@Component({
  selector: 'app-user-layout',
  imports: [
    RouterOutlet,
    HeaderComponent,
    StepMasterComponent,
    NgClass,
    AsyncPipe,
  ],
  templateUrl: './user-layout.component.html',
  styleUrl: './user-layout.component.scss',
})
export class UserLayoutComponent
  implements OnInit, OnDestroy, CanComponentDeactivate
{
  #globalStore = inject(Store);
  #destroyRef = inject(DestroyRef);
  sessionService = inject(ManageSessionService);
  #activeRoute = inject(ActivatedRoute);
  #router = inject(Router);

  isInitializeDiagnosisScreen = signal(false);
  sessionId: string = '';
  externalId: string = '';
  isVDR$: Observable<boolean>;
  private _sub: Subscription = new Subscription();

  constructor() {
    this.#activeRoute.firstChild?.params
      .pipe(takeUntilDestroyed())
      .subscribe((param) => {
        this.sessionId = param['sid'] || '';
      });

    this.#router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map((event) => event.url),
      )
      .subscribe((url) => {
        const [_, path, id] = url.split('/');
        if (path === 'eid') {
          this.externalId = id;
        } else {
          this.sessionId = id;
        }
      });

    // URL to open link from VDR: /select-solutions/:sid?source=vdr
    this.isVDR$ = this.#activeRoute.queryParams.pipe(
      map((params) => params['source'] === SOURCE.VDR),
    );
  }

  ngOnInit(): void {
    this.getScreenState();
    this._sub = fromEvent(window, 'beforeunload').subscribe(() => {
      this.canDeactivate();
    });
  }

  getScreenState(): void {
    this.#globalStore
      .select(selectScreenState)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((screenState) => {
        this.isInitializeDiagnosisScreen.set(screenState);
      });
  }

  canDeactivate(): CanDeactivateType {
    if (!this.sessionId) return true;
    return this.sessionService
      .unblock(this.sessionId)
      .pipe(map((resp) => !resp.isBlocking));
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
  }
}
